import { clamp01 } from "../math.js";
import { PALETTE_SIZE } from "../data/types.js";
import {
  createShaderProgram,
  createUnitQuad,
  paletteUniformData,
  requireAttribute,
  requireBuffer,
  UniformLocations,
} from "./shader-program.js";
import type { RenderLayer } from "./types.js";
import type { ClipTransform } from "./layout.js";
import type { RenderState } from "./render-state.js";
import { MAX_RIPPLES, RIPPLE_BAND_GLSL } from "./render-state.js";
import { ORIGIN_X, ORIGIN_Y } from "./dot-field.js";
import { BLEND_GLSL, blendIndex, type BlendMode } from "./blend.js";

/* The lens's color drift, drawn as instanced WebGL2 dots. The 877 sunglass-lens dots stay put;
   each one cycles its color through a ramp of the lens's own colors. A plasma of three sines in
   the vertex shader drives it every frame, scaled by each dot's edge mask, so the outline stays
   crisp while the inside churns. It draws on its own canvas, over the face. */

/** How fast the colors churn, as a multiplier on `uNow` (1.2 is 20% faster than the original). */
const LENS_CHURN = 1.2;

/** The color cycle: rank amplitude, angular frequencies (radians per second), and spatial
    frequencies (radians per artwork unit). They're baked into the shader as constants. */
const COLCYC = {
  amp: 5.6,
  w1: 0.34,
  w2: 0.55,
  w3: 0.78,
  kx1: 0.011,
  ky1: 0.008,
  kx2: -0.009,
  ky2: 0.013,
} as const;

/** Everything each lens dot needs, all in the same index order. `lensPal` holds palette indices
    (into the whole theme palette), sorted dark to light, and `lensPalN` is its length. */
export interface LensFieldData {
  count: number;
  x: Float32Array;
  y: Float32Array;
  r: Float32Array;
  baseRank: Int32Array;
  ph: Float32Array;
  ph2: Float32Array;
  ampJ: Float32Array;
  core: Float32Array;
  lensPal: readonly number[];
  lensPalN: number;
}

const g = (v: number): string => (Number.isInteger(v) ? `${v}.0` : `${v}`);

const VERTEX_SHADER_SRC = `#version 300 es
in vec2 aCorner;
in vec2 aCenter;    // dot center, artwork units
in float aRadius;
in float aBaseRank; // home rank in lensPal
in float aPh;       // primary phase
in float aPh2;      // independent phase
in float aAmpJ;     // amplitude jitter
in float aCore;     // edge mask [0,1]
uniform vec4 uTransform;
uniform float uMinRadius;
uniform float uNow;
uniform int uReduced;
uniform int uLensPalN;
uniform vec4 uLensBounds; // min x/y, then span x/y in artwork units
uniform float uSheenStrength;
// The waves (the same ones the face gets). The lens rides them, gently.
uniform vec2 uOrigin;
uniform float uArtworkToPage;
uniform int uRippleCount;
uniform vec2 uRing[${MAX_RIPPLES}];  // each wave's ring (RenderState.rings), in page pixels
uniform float uAmplitude[${MAX_RIPPLES}];
out vec2 vCorner;
flat out int vRank;
flat out float vReflectionAmount;
flat out vec3 vReflectionColor;

const float AMP = ${g(COLCYC.amp)};
const float W1 = ${g(COLCYC.w1)}, W2 = ${g(COLCYC.w2)}, W3 = ${g(COLCYC.w3)};
const float KX1 = ${g(COLCYC.kx1)}, KY1 = ${g(COLCYC.ky1)};
const float KX2 = ${g(COLCYC.kx2)}, KY2 = ${g(COLCYC.ky2)};

${RIPPLE_BAND_GLSL}
vec3 hueRgb(float hue) {
  float h = mod(mod(hue, 360.0) + 360.0, 360.0) / 60.0;
  float x = 1.0 - abs(mod(h, 2.0) - 1.0);
  vec3 channels;
  if (h < 1.0) channels = vec3(1.0, x, 0.0);
  else if (h < 2.0) channels = vec3(x, 1.0, 0.0);
  else if (h < 3.0) channels = vec3(0.0, 1.0, x);
  else if (h < 4.0) channels = vec3(0.0, x, 1.0);
  else if (h < 5.0) channels = vec3(x, 0.0, 1.0);
  else channels = vec3(1.0, 0.0, x);
  return vec3(0.37255) + channels * 0.62745;
}

// The most the lens dots shuffle, in page pixels. It's a fraction of the face's, so the
// reflection only shivers as the wave crosses (aCore keeps the edge crisp).
const float LENS_SHUFFLE_PX = 4.5;

void main() {
  vCorner = aCorner;
  vReflectionAmount = 0.0;
  vReflectionColor = vec3(1.0);
  float reflectionScale = 1.0;
  float rank = aBaseRank;
  vec2 pos = aCenter;
  if (uReduced == 0) {
    float x = aCenter.x, y = aCenter.y;
    float t = uNow * ${g(LENS_CHURN)}; // colors only; the wave below stays in sync
    float off = AMP * aAmpJ * (
          sin(t*W1 + x*KX1 + y*KY1 + aPh)
      + 0.6*sin(t*W2 + x*KX2 + y*KY2 + aPh2)
      + 0.4*sin(t*W3 + aPh2*2.3 + aPh) );
    rank = aBaseRank + floor(off * aCore + 0.5);   // round

    if (uRippleCount > 0) {
      // Each wave's ring around the butterfly, the same as the face's.
      float infl = 0.0;
      vec2 fromOrigin = (aCenter - uOrigin) * uArtworkToPage;
      for (int k = 0; k < ${MAX_RIPPLES}; k++) {
        if (k >= uRippleCount) break;
        infl += rippleInfluence(ringOffset(fromOrigin, uRing[k]), uAmplitude[k]);
      }
      // A light shimmer inside the lens (aCore holds the edge): each dot jitters as much as the
      // band says, so it follows the wave's ring instead of drifting in blobs.
      float shuf = min(infl, 1.4) * aCore * LENS_SHUFFLE_PX / uArtworkToPage;
      pos.x += cos(aPh * 7.3) * shuf;
      pos.y += sin(aPh * 5.1) * shuf;
    }

    if (uSheenStrength > 0.0) {
      vec2 lens = (aCenter - uLensBounds.xy) / uLensBounds.zw;
      float oilWave = sin(lens.x * 11.0 + lens.y * 8.0 - t * 1.7);
      float oil = clamp(0.36 + oilWave * 0.32, 0.0, 1.0) * uSheenStrength;
      float outlineHold = 0.28 + 0.72 * aCore;
      vReflectionAmount = clamp(oil * outlineHold, 0.0, 1.0);
      vReflectionColor = hueRgb(180.0 + sin(lens.x * 5.0 - lens.y * 4.0 + t) * 120.0);
      reflectionScale = 1.0 + aCore * (vReflectionAmount * 0.28);
    }
  }
  vRank = int(clamp(rank, 0.0, float(uLensPalN - 1)));
  float rClip = max(uMinRadius, aRadius * abs(uTransform.x)) * reflectionScale;
  vec2 c = vec2(pos.x*uTransform.x + uTransform.z, pos.y*uTransform.y + uTransform.w);
  gl_Position = vec4(c + aCorner*rClip, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SRC = `#version 300 es
precision highp float;
in vec2 vCorner;
flat in int vRank;
flat in float vReflectionAmount;
flat in vec3 vReflectionColor;
uniform vec3 uLensPal[${PALETTE_SIZE}];
uniform int uSheen;      // 1 draws only the sheen, as premultiplied color over the dots
out vec4 fragColor;
${BLEND_GLSL}
void main() {
  // The dot fades out over one device pixel at its edge, like the face dots (dot-field.ts).
  float edge = length(vCorner);
  float aa = 0.5 * fwidth(edge);
  float coverage = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, edge);
  if (coverage <= 0.0) discard;
  if (uSheen == 1) {
    if (vReflectionAmount <= 0.0) discard;
    fragColor = vec4(vReflectionColor * vReflectionAmount, vReflectionAmount) * coverage;
    return;
  }
  vec3 ink = blend(uGround, mix(uLensPal[vRank], vReflectionColor, vReflectionAmount));
  fragColor = vec4(ink, 1.0) * coverage;
}
`;

const FLOATS_PER_DOT = 8; // x, y, r, baseRank, ph, ph2, ampJ, core

const LENS_FIELD_UNIFORMS = [
  "uTransform",
  "uMinRadius",
  "uNow",
  "uReduced",
  "uLensPalN",
  "uLensPal",
  "uLensBounds",
  "uSheenStrength",
  "uOrigin",
  "uArtworkToPage",
  "uRippleCount",
  "uRing",
  "uAmplitude",
  "uSheen",
  "uBlend",
  "uGround",
] as const;

export class LensFieldLayer implements RenderLayer {
  private readonly gl: WebGL2RenderingContext;
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly quadBuffer: WebGLBuffer;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly u: UniformLocations<(typeof LENS_FIELD_UNIFORMS)[number]>;
  private readonly instanceCount: number;
  private readonly lensPal: readonly number[];
  private readonly lensPalN: number;

  static async create(
    gl: WebGL2RenderingContext,
    data: LensFieldData,
    signal?: AbortSignal,
  ): Promise<LensFieldLayer> {
    const program = await createShaderProgram(gl, VERTEX_SHADER_SRC, FRAGMENT_SHADER_SRC, signal);
    try {
      signal?.throwIfAborted();
      return new LensFieldLayer(gl, data, program);
    } catch (error) {
      gl.deleteProgram(program);
      throw error;
    }
  }

  private constructor(gl: WebGL2RenderingContext, data: LensFieldData, program: WebGLProgram) {
    this.gl = gl;
    this.lensPal = data.lensPal;
    this.lensPalN = data.lensPalN;

    const cleanup: Array<() => void> = [];
    try {
      const u = new UniformLocations(gl, program, LENS_FIELD_UNIFORMS, "LensFieldLayer");

      const aCorner = requireAttribute(gl, program, "aCorner", "LensFieldLayer");
      const aCenter = requireAttribute(gl, program, "aCenter", "LensFieldLayer");
      const aRadius = requireAttribute(gl, program, "aRadius", "LensFieldLayer");
      const aBaseRank = requireAttribute(gl, program, "aBaseRank", "LensFieldLayer");
      const aPh = requireAttribute(gl, program, "aPh", "LensFieldLayer");
      const aPh2 = requireAttribute(gl, program, "aPh2", "LensFieldLayer");
      const aAmpJ = requireAttribute(gl, program, "aAmpJ", "LensFieldLayer");
      const aCore = requireAttribute(gl, program, "aCore", "LensFieldLayer");

      const vao = gl.createVertexArray();
      if (!vao) throw new Error("LensFieldLayer: gl.createVertexArray returned null");
      cleanup.push(() => gl.deleteVertexArray(vao));
      gl.bindVertexArray(vao);

      const quadBuffer = createUnitQuad(gl, aCorner, "LensFieldLayer");
      cleanup.push(() => gl.deleteBuffer(quadBuffer));

      const n = data.count;
      const inst = new Float32Array(n * FLOATS_PER_DOT);
      for (let i = 0; i < n; i++) {
        const o = i * FLOATS_PER_DOT;
        inst[o] = data.x[i] ?? 0;
        inst[o + 1] = data.y[i] ?? 0;
        inst[o + 2] = data.r[i] ?? 0;
        inst[o + 3] = data.baseRank[i] ?? 0;
        inst[o + 4] = data.ph[i] ?? 0;
        inst[o + 5] = data.ph2[i] ?? 0;
        inst[o + 6] = data.ampJ[i] ?? 0;
        inst[o + 7] = data.core[i] ?? 0;
      }
      const instanceBuffer = requireBuffer(gl, "LensFieldLayer instance");
      cleanup.push(() => gl.deleteBuffer(instanceBuffer));
      gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, inst, gl.STATIC_DRAW);

      const stride = FLOATS_PER_DOT * Float32Array.BYTES_PER_ELEMENT;
      const F = Float32Array.BYTES_PER_ELEMENT;
      const attr = (loc: number, size: number, offsetFloats: number): void => {
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offsetFloats * F);
        gl.vertexAttribDivisor(loc, 1);
      };
      attr(aCenter, 2, 0);
      attr(aRadius, 1, 2);
      attr(aBaseRank, 1, 3);
      attr(aPh, 1, 4);
      attr(aPh2, 1, 5);
      attr(aAmpJ, 1, 6);
      attr(aCore, 1, 7);

      gl.bindVertexArray(null);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);

      gl.useProgram(program);
      gl.uniform1i(u.get("uLensPalN"), data.lensPalN);
      gl.uniform2f(u.get("uOrigin"), ORIGIN_X, ORIGIN_Y); // the waves' origin, in artwork units
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < n; i++) {
        const x = data.x[i] ?? 0,
          y = data.y[i] ?? 0;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
      if (n === 0) {
        minX = 0;
        minY = 0;
        maxX = 1;
        maxY = 1;
      }
      gl.uniform4f(
        u.get("uLensBounds"),
        minX,
        minY,
        Math.max(maxX - minX, 1),
        Math.max(maxY - minY, 1),
      );

      this.program = program;
      this.u = u;
      this.vao = vao;
      this.quadBuffer = quadBuffer;
      this.instanceBuffer = instanceBuffer;
      this.instanceCount = n;
    } catch (err) {
      for (const free of cleanup.reverse()) free();
      throw err;
    }
  }

  /** Looks up the `lensPal` indices in `themeColors`, the whole theme palette (light `#rrggbb` or
      dark `rgb()`), and sends the colors to `uLensPal`. */
  setPalette(themeColors: readonly string[]): void {
    const colors = this.lensPal.slice(0, this.lensPalN).map((index) => themeColors[index]);
    this.gl.useProgram(this.program);
    this.gl.uniform3fv(this.u.get("uLensPal"), paletteUniformData(colors, PALETTE_SIZE));
  }

  /** Blends the dots over `ground` (sRGB, 0 to 1), the page color behind them, the way the CSS
      blend `mode` would (blend.ts). The sheen doesn't blend. */
  setBlend(mode: BlendMode, ground: readonly [number, number, number]): void {
    this.gl.useProgram(this.program);
    this.gl.uniform1i(this.u.get("uBlend"), blendIndex(mode));
    this.gl.uniform3f(this.u.get("uGround"), ...ground);
  }

  setTransform(t: ClipTransform): void {
    this.gl.useProgram(this.program);
    this.gl.uniform4f(this.u.get("uTransform"), t.sx, t.sy, t.ox, t.oy);
    this.gl.uniform1f(this.u.get("uMinRadius"), t.minRadius);
  }

  resize(deviceW: number, deviceH: number): void {
    this.gl.viewport(0, 0, deviceW, deviceH);
  }

  /** Draws the lens dots, then (unless motion is reduced) the sheen over them: one program, two
      passes. `sheenStrength`, 0 to 1, is how strong the oil-film sheen is. */
  render(state: RenderState, sheenStrength = 0): void {
    const gl = this.gl;
    const strength = state.reducedMotion ? 0 : clamp01(sheenStrength);
    gl.useProgram(this.program);
    gl.uniform1f(this.u.get("uNow"), state.time);
    gl.uniform1i(this.u.get("uReduced"), state.reducedMotion ? 1 : 0);

    // The face's waves, so the lens shivers as each front crosses it.
    const rippleCount = Math.min(state.rippleCount, MAX_RIPPLES);
    gl.uniform1f(this.u.get("uArtworkToPage"), state.artworkToPage);
    gl.uniform1i(this.u.get("uRippleCount"), rippleCount);
    gl.uniform2fv(this.u.get("uRing"), state.rings);
    gl.uniform1fv(this.u.get("uAmplitude"), state.amplitudes);

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    // The sheen and the dots' soft edges are premultiplied, so the sheen pass lands on the dots
    // the same as if it had its own canvas.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.vao);
    // The dots never take the sheen.
    gl.uniform1i(this.u.get("uSheen"), 0);
    gl.uniform1f(this.u.get("uSheenStrength"), 0);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
    if (strength > 0) {
      gl.uniform1i(this.u.get("uSheen"), 1);
      gl.uniform1f(this.u.get("uSheenStrength"), strength);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
    }
    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteBuffer(this.quadBuffer);
    gl.deleteBuffer(this.instanceBuffer);
    gl.deleteVertexArray(this.vao);
  }
}
